import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Phone } from 'lucide-react';
import { packages } from '../utils/packageData';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const PackageSpecs = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const allFeatures = [
    'Ceiling Height', 'Square Footage Range', 'Insulation', 'Water Supply Pipe',
    'Drywall', 'Windows', 'Paint', 'Electrical Panel', 'Roofing', 'Exterior Door',
    'Interior Door', 'Flooring', 'Cabinet Style', 'Backsplash', 'Range Hood',
    'Stove/Oven', 'Toilet', 'Lighting', 'HVAC System', 'Smart Lock',
    'Security Camera', 'Smart Faucet', 'Smart Switch/Outlet', 'Skylight', 'Custom Furniture'
  ];

  const getFeatureValue = (pkg, featureName) => {
    const featureMap = {
      'Ceiling Height': pkg.version === 'V1.0' ? '8ft' : pkg.version === 'V2.0' ? '9ft' : '9ft + Vaulted',
      'Square Footage Range': '800-1200 SQFT',
      'Insulation': 'JM Fiberglass',
      'Water Supply Pipe': 'Copper',
      'Drywall': '5/8"',
      'Windows': 'Double Window',
      'Paint': 'Dunn Edwards',
      'Electrical Panel': pkg.version === 'V1.0' || pkg.version === 'V2.0' ? '100Amp' : '100-200Amp',
      'Roofing': pkg.version === 'V1.0' || pkg.version === 'V2.0' ? 'Shingle' : 'Shingle/Tile',
      'Exterior Door': pkg.version === 'V1.0' || pkg.version === 'V2.0' ? 'Standard' : 'Luxury Black Metal',
      'Interior Door': pkg.version === 'V1.0' || pkg.version === 'V2.0' ? 'Standard' : pkg.version === 'V3.0' ? 'Luxury 82"' : 'Luxury 96"',
      'Flooring': pkg.version === 'V1.0' || pkg.version === 'V2.0' ? '6.5mm Vinyl' : pkg.version === 'V3.0' ? '8.0mm Vinyl' : '10.0mm Vinyl',
      'Cabinet Style': pkg.version === 'V1.0' || pkg.version === 'V2.0' ? 'American Style' : 'European Style',
      'Backsplash': pkg.version === 'V1.0' ? '-' : 'Stone Splash',
      'Range Hood': pkg.version === 'V1.0' ? 'Standard' : pkg.version === 'V2.0' ? 'High-power' : 'FOTILE',
      'Stove/Oven': pkg.version === 'V1.0' ? 'Stovetop Only' : 'Stove with Oven',
      'Toilet': pkg.version === 'V1.0' ? 'Standard' : pkg.version === 'V2.0' ? 'One-piece x2' : 'Smart x2',
      'Lighting': 'LED Light',
      'HVAC System': pkg.version === 'V1.0' || pkg.version === 'V2.0' ? 'Mini Split AC' : 'Central AC',
      'Smart Lock': pkg.version === 'V4.0' ? '✓' : '-',
      'Security Camera': pkg.version === 'V4.0' ? '✓' : '-',
      'Smart Faucet': pkg.version === 'V4.0' ? '✓' : '-',
      'Smart Switch/Outlet': pkg.version === 'V4.0' ? '✓' : '-',
      'Skylight': pkg.version === 'V4.0' ? 'Venting Skylight' : '-',
      'Custom Furniture': pkg.version === 'V4.0' ? 'Foldable Bed' : '-'
    };
    return featureMap[featureName] || '-';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50">
      <SEO
        title={isZh ? 'ADU 套餐对比 V1-V4 | 加州 ADU 智能豪华套餐报价 | ALIGCUS 阿里建筑' : 'ADU Package Comparison V1-V4 | California ADU Pricing & Smart Luxury Specs | ALIGCUS'}
        description={isZh ? '对比 ALIGCUS 4 款 ADU 套餐：标准 V1.0、舒适 V2.0、豪华 V3.0、智能豪华 V4.0。详细规格、地板、橱柜、智能家居配置一览。免费报价 (626) 803-8000。' : 'Compare ALIGCUS 4 ADU packages: Standard V1.0, Comfort V2.0, Luxury V3.0, Smart Luxury V4.0. Detailed specs for flooring, cabinets, smart home features. Free quote (626) 803-8000.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/packages' : 'https://www.aligcus.com/packages'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/packages' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/packages' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? 'ADU 套餐' : 'ADU Packages', url: isZh ? 'https://www.aligcus.com/zh/packages' : 'https://www.aligcus.com/packages' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: isZh ? 'ALIGCUS ADU 套餐系列' : 'ALIGCUS ADU Package Series',
            description: isZh ? '从标准到智能豪华，4 款 ADU 套餐覆盖不同预算' : 'From Standard to Smart Luxury, 4 ADU packages for every budget',
            brand: { '@type': 'Brand', name: 'ALIGCUS' },
            image: 'https://www.aligcus.com/api/uploads/f823eb4b3952.jpg',
            offers: {
              '@type': 'AggregateOffer',
              priceCurrency: 'USD',
              lowPrice: '150000',
              highPrice: '400000',
              offerCount: '4',
              availability: 'https://schema.org/InStock',
            },
          },
        ]}
      />
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-12">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">{t('packages.title')}</h1>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">{t('packages.subtitlePlain')}</p>
        </div>

        {/* Package Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {packages.map((pkg) => (
            <Card key={pkg.value} className="shadow-xl hover:shadow-2xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="text-sm font-semibold text-red-600 mb-2">{pkg.version}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                <p className="text-sm text-gray-600">{pkg.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Comparison Table */}
        <Card className="shadow-2xl border-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-red-600 to-red-700 text-white">
                  <th className="p-4 text-left font-bold min-w-[200px]">Feature</th>
                  {packages.map((pkg) => (
                    <th key={pkg.value} className="p-4 text-center font-bold min-w-[150px]">
                      <div>{pkg.version}</div>
                      <div className="text-sm font-normal">{pkg.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((feature, idx) => (
                  <tr key={feature} className={`${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-red-50 transition-colors`}>
                    <td className="p-4 font-medium text-gray-900 border-r border-gray-200">{feature}</td>
                    {packages.map((pkg) => {
                      const value = getFeatureValue(pkg, feature);
                      return (
                        <td key={pkg.value} className="p-4 text-center text-gray-700">
                          {value === '✓' ? <Check className="w-6 h-6 text-green-600 mx-auto" /> : value === '-' ? <span className="text-gray-400">-</span> : <span className="font-medium">{value}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Smart Features Highlight */}
        <Card className="mt-12 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 shadow-xl">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">V4.0 Smart Luxury Exclusive Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {['Smart Door Lock System', 'HD Security Camera System', 'Smart Touchless Faucet', 'Smart Light Switches', 'Smart Power Outlets', 'Venting Skylight', '1856 Custom Built Foldable Bed', 'Complete Home Automation'].map((feature) => (
                <div key={feature} className="flex items-center bg-white p-3 rounded-lg shadow">
                  <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="mt-12 text-center bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-12 shadow-2xl">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Build Your Dream ADU?</h2>
          <p className="text-red-100 mb-8 text-lg">Get a customized quote in minutes</p>
          <div className="flex justify-center">
            <Link to="/quote">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg">
                {t('nav.getQuote')}
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3 justify-center mt-6 max-w-lg mx-auto">
            {[{l:'SoCal',n:'(626) 803-8000',t:'6268038000'},{l:'NorCal',n:'(408) 900-6688',t:'4089006688'},{l:'San Diego',n:'(858) 910-0000',t:'8589100000'}].map(p=>(
              <a key={p.l} href={`tel:${p.t}`} className="flex flex-col items-center gap-1 px-4 py-3 bg-white/10 border border-white/30 rounded-xl hover:bg-white/20 transition-all">
                <span className="text-xs font-bold text-white/80">{p.l}</span>
                <span className="text-sm font-bold text-white">{p.n}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PackageSpecs;
